import {
    createContext,
    useContext,
    useState,
    PropsWithChildren,
    useEffect,
    useMemo,
} from "react";
import { InvoiceQueryParams } from "../common/contracts/invoice.contract";
import {
    InvoiceViewOptions,
    InvoiceViewOptionGroup,
    InvoiceStats,
} from "../common/types/invoice";
import { useInvoicesPageFiltersCache } from "../hooks/useInvoicesPageFiltersCache";
import { useInvoicesPageViewOptionsCache } from "../hooks/useInvoicesPageViewOptionsCache";
import { defaultInvoiceStatuses } from "../components/pageHeader/filters/lib";
import { useInvoiceStats } from "../api/invoice.api";

interface InvoicesViewContextValue {
    filters: InvoiceQueryParams["filters"];
    setFilters: React.Dispatch<
        React.SetStateAction<InvoiceQueryParams["filters"]>
    >;

    viewOption: InvoiceViewOptions;
    setViewOption: React.Dispatch<React.SetStateAction<InvoiceViewOptions>>;
    invoiceStats?: InvoiceStats;
}

const defaultViewOption: InvoiceViewOptions = {
    groupBy: InvoiceViewOptionGroup.COUNTERPARTY,
};

const InvoicesViewContext = createContext<InvoicesViewContextValue | null>(
    null,
);

export const InvoicesViewProvider: React.FC<PropsWithChildren> = ({
    children,
}) => {
    const [cachedFilters, setCachedFilters] = useInvoicesPageFiltersCache();
    const [cachedViewOptions, setCachedViewOptions] =
        useInvoicesPageViewOptionsCache();

    const [filters, setFilters] = useState<InvoiceQueryParams["filters"]>(
        () => ({
            invoiceStatuses: defaultInvoiceStatuses,
            ...cachedFilters,
        }),
    );

    const [viewOption, setViewOption] = useState<InvoiceViewOptions>(
        () => cachedViewOptions ?? defaultViewOption,
    );

    useEffect(() => {
        setCachedFilters(filters);
    }, [filters, setCachedFilters]);

    useEffect(() => {
        setCachedViewOptions(viewOption);
    }, [viewOption, setCachedViewOptions]);

    const { data: invoiceStats } = useInvoiceStats({
        filters,
    });

    const contextValue = useMemo(
        () => ({
            filters,
            setFilters,
            viewOption,
            setViewOption,
            invoiceStats: invoiceStats?.data,
        }),
        [filters, setFilters, viewOption, setViewOption, invoiceStats?.data],
    );

    return (
        <InvoicesViewContext.Provider value={contextValue}>
            {children}
        </InvoicesViewContext.Provider>
    );
};

export function useInvoicesView() {
    const context = useContext(InvoicesViewContext);
    if (!context) {
        throw new Error(
            "useInvoicesView must be used within InvoicesViewProvider",
        );
    }
    return context;
}
